import React, { useState } from 'react';
import { Alert, Button, Spinner } from 'reactstrap';
import { componentRows } from './componentProfileData';
import { hasInventory, formatFetchedAt } from './fleetPanelData';

const devices = n => `${n} device${n === 1 ? '' : 's'}`;

/**
 * Which modules the fleet runs, and the reviewer's overrides.
 *
 * Rendered as a card in the project view's existing list idiom, not as a new
 * kind of surface — this is an addition to the page, not a redesign of it.
 *
 * Modules no device runs are hidden behind a toggle. A large fleet's product
 * can carry dozens of modules of which a handful are in use, and a grid
 * showing all of them buries the ones that matter under the ones that do not.
 */
const ComponentProfilePanel = ({ project, onToggle, saving, toggleError, onRefresh, refreshing, refreshError }) => {
  const [showUnused, setShowUnused] = useState(false);
  if (!hasInventory(project)) return null;

  const rows = componentRows(project);
  const used = rows.filter(r => r.enabledCiCount > 0 || r.isOverridden);
  const unused = rows.filter(r => !used.includes(r));
  const visible = showUnused ? rows : used;
  const when = formatFetchedAt((project.inventorySnapshot || {}).fetchedAt);

  return (
    <div className="bst-version-list mb-3">
      <div className="bst-version-list-header">
        <h3>Components in your fleet</h3>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small" title={when.title}>
            from your CMDB · updated {when.text}
          </span>
          {onRefresh && (
            <Button color="link" size="sm" className="p-0" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? <Spinner size="sm" /> : 'Refresh'}
            </Button>
          )}
        </div>
      </div>

      <div className="p-3">
        <p className="bst-component-explainer">
          Bugs and CVEs affecting only a module <strong>no device runs</strong> are marked <em>Config removed</em> and
          drop out of your reviews. Switch a module on or off to override what your CMDB reports.
        </p>

        {refreshError && (
          <Alert color="danger" role="alert">
            {refreshError}
          </Alert>
        )}
        {toggleError && (
          <Alert color="danger" role="alert">
            {toggleError}
          </Alert>
        )}

        {rows.length === 0 ? (
          <p className="text-muted mb-0">Your CMDB did not report module configuration for these devices.</p>
        ) : (
          <>
            <div className="bst-component-grid">
              {visible.map(row => (
                <div className="bst-component-tile" key={row.component}>
                  <div>
                    <div className="bst-component-tile-name">{row.component}</div>
                    <div className="bst-component-tile-count">
                      {row.inProfile ? (
                        <>
                          {row.enabledCiCount} of {devices(row.configuredCiCount)}
                        </>
                      ) : (
                        'not in the latest scan'
                      )}
                      {row.autoOff && ' · off automatically'}
                    </div>
                    {/* Shown, never acted on: an override is the reviewer's
                        decision and is not silently flipped by a later scan. */}
                    {row.conflict && (
                      <div className="bst-component-tile-conflict">
                        You turned this {row.enabled ? 'on' : 'off'}, but CI data reports it{' '}
                        {row.enabled ? 'disabled' : 'enabled'}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="bst-switch"
                    role="switch"
                    aria-checked={row.enabled}
                    aria-label={row.component}
                    disabled={saving}
                    onClick={() => onToggle(row.component, !row.enabled)}
                  />
                </div>
              ))}
            </div>

            {unused.length > 0 && (
              <Button color="link" size="sm" className="p-0 mt-3" onClick={() => setShowUnused(!showUnused)}>
                {showUnused ? 'Hide' : 'Show'} {unused.length} module{unused.length === 1 ? '' : 's'} no device runs
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ComponentProfilePanel;
